// src/context/brandVoice/widgets/PairListEditor.jsx — the `pair_list` editor: only
// `communication_principles` (the "Principi" tab), one row per principle with a NAME box
// and a DESCRIPTION box.
//
// ⭐ It edits ROWS `{ name, description }` and hands up a WHOLE new array on every change
// (immutable). The split into rows and the join back into the stored string live in
// `wireShape.js`, never here. ⛔ It validates nothing: the checks run on SAVE
// (`validateField`); `problems` is used here only to mark the offending row's inputs
// `aria-invalid` — the sentences are FieldRow's.
// ⛔ No real line breaks: the backend collapses every whitespace run into one space
// (sheet 08, common rule 1), so Enter is blocked in the description and a pasted newline
// becomes a space as it arrives. The description still WRAPS and grows with its text.
// ⛔ No reordering (decision 1, 2026-09-27): the order is the stored one.
// v186: GrowingTextarea / oneLine / the list styles are shared (ui/GrowingTextarea.jsx, widgets/listStyles.js).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { fromWire } from '../wireShape.js';
import GrowingTextarea, { blockEnter, oneLine } from '../ui/GrowingTextarea.jsx';
import { ADD, EMPTY, INPUT, REMOVE, ROW } from './listStyles.js';
import { fieldPlaceholder } from './fieldPlaceholder.js';
import { FONT_SANS } from '../../../constants/theme.js';

const EMPTY_ROW = { name: '', description: '' };

export default function PairListEditor({ field, value, onChange, disabled = false, problems = [] }) {
  const { t } = useTranslation('context');
  const rows = fromWire(field, value);
  const badRows = new Set((problems || []).map((p) => p?.params?.row).filter(Boolean));
  const replace = (index, patch) => onChange?.(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const remove = (index) => onChange?.(rows.filter((_, i) => i !== index));
  const add = () => onChange?.([...rows, EMPTY_ROW]);
  // v200: each placeholder names its box and gives an example (`fieldPlaceholder.js`).
  const placeholderOf = (column, fallbackKey) => fieldPlaceholder(t, { path: field?.field_path, column, fallbackKey });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontFamily: FONT_SANS }}>
      {rows.length === 0 && (
        <p style={EMPTY}>{t('brandVoice.pair.empty')}</p>
      )}
      {rows.map((row, i) => {
        const n = i + 1;
        const invalid = badRows.has(n);
        return (
          // No reordering, so the index is a stable key for the life of a row.
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} style={ROW}>
            <GrowingTextarea
              aria-label={t('brandVoice.pair.nameLabel', { row: n })}
              aria-invalid={invalid || undefined}
              placeholder={placeholderOf('name', 'brandVoice.pair.name')}
              fitPlaceholder
              value={row.name ?? ''}
              disabled={disabled}
              onKeyDown={blockEnter}
              onChange={(e) => replace(i, { name: oneLine(e.target.value) })}
              style={{ ...INPUT(invalid), flex: '0 1 190px', minWidth: 140, fontWeight: 600 }}
            />
            <GrowingTextarea
              aria-label={t('brandVoice.pair.descriptionLabel', { row: n })}
              aria-invalid={invalid || undefined}
              placeholder={placeholderOf('description', 'brandVoice.pair.description')}
              fitPlaceholder
              value={row.description ?? ''}
              disabled={disabled}
              onKeyDown={blockEnter}
              onChange={(e) => replace(i, { description: oneLine(e.target.value) })}
              style={{ ...INPUT(invalid), flex: '1 1 240px', minWidth: 0 }}
            />
            <button
              type="button"
              aria-label={t('brandVoice.pair.remove', { row: n })}
              disabled={disabled}
              onClick={() => remove(i)}
              style={REMOVE(disabled)}
            >
              <X size={16} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        );
      })}
      <div>
        <button type="button" disabled={disabled} onClick={add} style={ADD(disabled)}>
          {t('brandVoice.pair.add')}
        </button>
      </div>
    </div>
  );
}
